import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { EnrollmentPeriod } from 'types/enrollmentService';
import { Group } from 'types/groupService';
import { CopyHelper } from 'global/utils/CopyHelper';
import { INITIAL_GROUP_OBJECT } from 'store/domain/groupStore';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import {
  deleteEnrollment,
  getAllEnrollmentPeriodsByGroupId,
  getAllInactiveEnrollmentPeriodsByGroupId,
  sellEnrollment,
} from 'api/operations/enrollmentService';
import { COMMON_URLS } from '@insurancemenu/imenu-components-lib/dist/utils/urls';

export interface DynamicEnrollmentCard extends EnrollmentPeriod {
  loading: boolean;
}

export interface ShortCurrentPeriodInfo {
  periodId: string;
  effectiveDate: string;
}

export const INITIAL_CURRENT_PERIOD = Object.freeze({ periodId: '', effectiveDate: '' });

export class EnrollmentCardStore {
  rootStore: RootStore;
  loading = false;
  activePeriods: DynamicEnrollmentCard[] = [];
  inactivePeriods: EnrollmentPeriod[] = [];
  group: Group;
  showChangeEffectiveDateModal = false;
  /**
   * Period where effective date will be changed
   */
  currentPeriod: ShortCurrentPeriodInfo = INITIAL_CURRENT_PERIOD;

  private setLoadingStatus(status: boolean): void {
    this.loading = status;
  }

  public updateChangeEffectiveDateModalStatus(status: boolean, currentPeriod: ShortCurrentPeriodInfo): void {
    this.showChangeEffectiveDateModal = status;
    this.currentPeriod = currentPeriod;
  }

  public async collectEnrollmentPeriods(groupId: string): Promise<void> {
    this.setLoadingStatus(true);

    const requestsForPeriods = [
      getAllEnrollmentPeriodsByGroupId(groupId),
      getAllInactiveEnrollmentPeriodsByGroupId(groupId),
      this.rootStore.groupStore.getGroupById(groupId),
    ];

    const promisesResult = await Promise.all(requestsForPeriods);

    void runInAction(() => {
      this.activePeriods = (promisesResult[0] as EnrollmentPeriod[]).map((period) => {
        return { ...period, loading: false };
      });
      this.inactivePeriods = (promisesResult[1] as EnrollmentPeriod[]).sort(
        (a, b) => +new Date(b.effectiveDate) - +new Date(a.effectiveDate),
      );
      this.group = this.rootStore.groupStore.group;

      this.setLoadingStatus(false);
    });
  }

  async sellEnrollment(periodId: string): Promise<void> {
    this.setLoadingStatus(true);
    await sellEnrollment(periodId);
    this.setLoadingStatus(false);
  }

  async deleteEnrollment(periodId: string): Promise<void> {
    this.setLoadingStatus(true);
    await deleteEnrollment(periodId);
    this.setLoadingStatus(false);
  }

  public get showBenefitSummariesDownload(): boolean {
    // Altus only
    return this.rootStore.userStore.sponsorId === '244';
  }

  public downloadBenefitSummary(carrierProductId: string): void {
    downloadByUrl(
      `https://${COMMON_URLS.api}/proposalV3/Prod/initialSeedDownload?sponsorId=${this.rootStore.userStore.sponsorId}&carrierProductId=${carrierProductId}`,
    );
  }

  public getPeriodLoading(periodId: string): boolean {
    return this.getActivePeriod(periodId).loading;
  }

  private getActivePeriod(periodId: string): DynamicEnrollmentCard {
    const activePeriod = this.activePeriods.find((period) => period.id === periodId);
    if (activePeriod) {
      return activePeriod;
    }
    throw new Error(`No Period in with id: ${periodId}`);
  }

  getGroupLocally(group: Group): void {
    this.group = CopyHelper.deepCopy(group);
  }

  public resetInitialData(): void {
    this.activePeriods = [];
    this.inactivePeriods = [];
    this.group = INITIAL_GROUP_OBJECT;
    this.currentPeriod = INITIAL_CURRENT_PERIOD;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.group = INITIAL_GROUP_OBJECT;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
